import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface Application {
  id:                   string;
  application_number:   string;
  status:               string;
  applied_level:        string | null;
  student_full_name_en: string;
  student_ic_number:    string | null;
  guardian_name:        string;
  guardian_phone:       string;
  school_name:          string | null;
  submitted_at:         string | null;
}

interface PageResult {
  data:  Application[];
  total: number;
  page:  number;
  limit: number;
}

const STATUS_LABEL: Record<string, { en: string; ms: string; color: string }> = {
  draft:          { en: 'Draft',          ms: 'Draf',            color: colors.neutral.gray400 },
  submitted:      { en: 'Submitted',      ms: 'Dihantar',        color: '#60a5fa' },
  pending_review: { en: 'Pending Review', ms: 'Menunggu Semakan', color: '#facc15' },
  approved:       { en: 'Approved',       ms: 'Diluluskan',      color: '#4ade80' },
  rejected:       { en: 'Rejected',       ms: 'Ditolak',         color: '#f87171' },
  withdrawn:      { en: 'Withdrawn',      ms: 'Ditarik Balik',   color: colors.neutral.gray600 },
};

const LEVELS = ['', 'primary', 'secondary', 'upper-secondary'];

export function ApplicationsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const [data,    setData]    = useState<Application[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterLevel,  setFilterLevel]  = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [searchInput,  setSearchInput]  = useState('');

  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (filterStatus) params.set('status', filterStatus);
      if (filterLevel)  params.set('appliedLevel', filterLevel);
      if (filterSearch) params.set('search', filterSearch);

      const result = await apiGet<PageResult>(`/api/students/applications?${params}`);
      setData(result.data);
      setTotal(result.total);
    } catch (err) {
      const msg = err instanceof TypeError && err.message.includes('fetch')
        ? 'Tidak dapat menghubungi pelayan svc-sis. / Cannot reach svc-sis server.'
        : err instanceof Error ? err.message : 'Ralat tidak diketahui.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterLevel, filterSearch]);

  useEffect(() => { void load(); }, [load]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setFilterSearch(searchInput.trim());
    setPage(1);
  }

  function handleFilterChange() {
    setPage(1);
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div>

        {/* Header */}
        <div style={{ maxWidth: '1100px', margin: '0 auto', marginBottom: spacing.xl }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: spacing.md }}>
            <div>
              <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>
                {E ? '← Home' : '← Laman Utama'}
              </Link>
              <h1 style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                {E ? 'Enrolment Applications' : 'Permohonan Pendaftaran'}
              </h1>
              <p style={{ margin: 0, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                Student Enrolment Applications
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              {total > 0 && (
                <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                  {total} permohonan
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
                {loading ? <Spinner size="sm" /> : '↻ Muat Semula'}
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ maxWidth: '1100px', margin: '0 auto', marginBottom: spacing.lg, display: 'flex', flexWrap: 'wrap', gap: spacing.sm, alignItems: 'flex-end' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: spacing.xs }}>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cari nama / IC…"
              style={{
                backgroundColor: colors.neutral.charcoal,
                border:          `1px solid ${colors.neutral.gray800}`,
                borderRadius:    borderRadius.md,
                color:           colors.neutral.white,
                padding:         `${spacing.xs} ${spacing.sm}`,
                fontSize:        typography.fontSize.sm,
                width:           '220px',
                outline:         'none',
              }}
            />
            <Button type="submit" variant="secondary" size="sm" disabled={loading}>Cari</Button>
          </form>

          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); handleFilterChange(); }}
            style={selectStyle()}
          >
            <option value="">Semua Status</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l.ms} / {l.en}</option>
            ))}
          </select>

          <select
            value={filterLevel}
            onChange={(e) => { setFilterLevel(e.target.value); handleFilterChange(); }}
            style={selectStyle()}
          >
            {LEVELS.map((l) => (
              <option key={l} value={l}>{l || 'Semua Aras / All Levels'}</option>
            ))}
          </select>

          {(filterStatus || filterLevel || filterSearch) && (
            <Button variant="ghost" size="sm" onClick={() => {
              setFilterStatus(''); setFilterLevel(''); setFilterSearch(''); setSearchInput(''); setPage(1);
            }}>
              ✕ Kosongkan Penapis
            </Button>
          )}
        </div>

        {/* Content */}
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {error ? (
            <ErrorBanner message={error} onRetry={() => void load()} />
          ) : loading && data.length === 0 ? (
            <LoadingState />
          ) : data.length === 0 ? (
            <EmptyState filtered={!!(filterStatus || filterLevel || filterSearch)} />
          ) : (
            <>
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden', boxShadow: shadows.xl }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.neutral.gray800}` }}>
                      {['No. Permohonan', 'Pelajar / Student', 'Aras / Level', 'Wali / Guardian', 'Sekolah', 'Status', 'Tarikh / Date', ''].map((h) => (
                        <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400, whiteSpace: 'nowrap' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((app, i) => {
                      const sl = STATUS_LABEL[app.status];
                      return (
                        <tr
                          key={app.id}
                          style={{
                            borderBottom: i < data.length - 1 ? `1px solid ${colors.neutral.gray800}` : 'none',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                        >
                          <td style={tdStyle()}>{app.application_number}</td>
                          <td style={tdStyle()}>
                            <div style={{ fontWeight: typography.fontWeight.medium, color: colors.neutral.white }}>{app.student_full_name_en}</div>
                            {app.student_ic_number && (
                              <div style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{app.student_ic_number}</div>
                            )}
                          </td>
                          <td style={tdStyle()}>{app.applied_level ?? '—'}</td>
                          <td style={tdStyle()}>
                            <div>{app.guardian_name}</div>
                            <div style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{app.guardian_phone}</div>
                          </td>
                          <td style={tdStyle()}>{app.school_name ?? <span style={{ color: colors.neutral.gray600 }}>—</span>}</td>
                          <td style={tdStyle()}>
                            <span style={{
                              display: 'inline-block', padding: `2px ${spacing.xs}`,
                              backgroundColor: `${sl?.color ?? '#888'}22`,
                              color: sl?.color ?? colors.neutral.gray400,
                              borderRadius: borderRadius.full,
                              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
                            }}>
                              {(E ? sl?.en : sl?.ms) ?? app.status}
                            </span>
                          </td>
                          <td style={{ ...tdStyle(), fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString('ms-BN') : '—'}
                          </td>
                          <td style={tdStyle()}>
                            <Link to={`/admin/applications/${app.id}`} style={{ color: colors.primary.yellow, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>
                              Lihat →
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: spacing.sm, marginTop: spacing.lg, alignItems: 'center' }}>
                  <Button variant="ghost" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page <= 1 || loading}>‹ Sebelum</Button>
                  <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                    Halaman {page} / {totalPages}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages || loading}>Seterus ›</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function selectStyle(): React.CSSProperties {
  return {
    backgroundColor: colors.neutral.charcoal,
    border:          `1px solid ${colors.neutral.gray800}`,
    borderRadius:    borderRadius.md,
    color:           colors.neutral.white,
    padding:         `${spacing.xs} ${spacing.sm}`,
    fontSize:        typography.fontSize.sm,
    outline:         'none',
    cursor:          'pointer',
  };
}

function tdStyle(): React.CSSProperties {
  return {
    padding:   `${spacing.sm} ${spacing.md}`,
    fontSize:  typography.fontSize.sm,
    color:     colors.neutral.gray200,
    verticalAlign: 'middle',
  };
}

function LoadingState() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}>
      <Spinner size="lg" />
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.neutral.gray400 }}>
      <div style={{ fontSize: '40px', marginBottom: spacing.md }}>📋</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
        {filtered ? 'Tiada permohonan sepadan' : 'Tiada permohonan lagi'}
      </p>
      <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.sm }}>
        {filtered ? 'No applications match your filters.' : 'No applications have been submitted yet.'}
      </p>
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{
      backgroundColor: 'rgba(239,68,68,0.08)',
      border:          '1px solid rgba(239,68,68,0.3)',
      borderRadius:    borderRadius.lg,
      padding:         spacing.lg,
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'space-between',
      gap:             spacing.md,
    }}>
      <div>
        <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: '#f87171' }}>Ralat / Error</p>
        <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{message}</p>
      </div>
      <Button variant="secondary" size="sm" onClick={onRetry}>Cuba Lagi / Retry</Button>
    </div>
  );
}
